import React from "react";
import { t } from "ttag";

import { assertNever } from "@thelabnyc/thelabui/src/utils/never";

import { ButtonPrimary } from "../../Clickables";
import { ModalFooter } from "../ModalFooter/ModalFooter";
import { useProductContext } from "../ProductContext/ProductContext";
import { ResponsiveLabel } from "../ResponsiveLabel/ResponsiveLabel";
import type {
    Action,
    NavigationTarget,
    SKU,
    ScreenOption,
    WalkAppState,
} from "../engine";
import { getCurrentScreen, getSkipTarget, isBackVisible } from "../engine";
import { OptionTile } from "./OptionTile";

import styles from "./Screen.module.scss";

/**
 * Maps a `getSkipTarget` result to the Skip button's user-facing label.
 * `null` (populated entry) reads as "Cancel".
 */
const skipLabelFor = (target: NavigationTarget | null): string => {
    if (target === null) return t`Cancel`;
    switch (target.kind) {
        case "next-chain-entry":
            return target.targetLabel
                ? t`Skip to ${target.targetLabel}`
                : t`Skip`;
        case "next-spoke":
            return t`Skip to ${target.targetLabel}`;
        case "complete":
            return t`Skip and Complete`;
    }
    return assertNever(target);
};

export type ScreenProps = {
    state: WalkAppState;
    dispatch: React.Dispatch<Action>;
    onShowOfferDetails: () => void;
    /** Opens the PDP details modal */
    onShowProductDetails: (
        sku: SKU,
        onAdd: () => void,
        addLabel: string,
    ) => void;
};

export const Screen = ({
    state,
    dispatch,
    onShowOfferDetails,
    onShowProductDetails,
}: ScreenProps) => {
    const walk = state.walk;
    const { catalog, locale } = useProductContext();
    const screen = getCurrentScreen(state, catalog, locale);
    if (!screen) return null;
    const isAny = screen.pickMode === "any";
    const anySelectedCount = screen.options.filter((o) => o.isSelected).length;
    // Single-option pick:any (the back-guard / duct-cover / trim-kit
    // accessory screens) is "tap-to-pick-and-advance" per design — one
    // tile click both selects the accessory and submits the pending
    // contribution. Multi-option pick:any (none in the launch graph,
    // kept for forward-compat) keeps the toggle + Continue model.
    const isSingleOptionAny = isAny && screen.options.length === 1;

    const selectOption = (option: ScreenOption) => {
        if (isSingleOptionAny) {
            dispatch({ type: "PICK_ANY_AND_SUBMIT", optionId: option.id });
        } else if (isAny) {
            dispatch({ type: "TOGGLE_ANY", optionId: option.id });
        } else {
            dispatch({ type: "PICK_ONE", optionId: option.id });
        }
    };

    // CTA commits ("Add to Your Suite") when the tile carries the product, or
    // routes onward ("Continue") when picking it only advances the walk.
    const detailsHandlerFor = (
        option: ScreenOption,
    ): (() => void) | undefined => {
        const sku = option.detailsSku;
        if (!sku) return undefined;
        return () =>
            onShowProductDetails(
                sku,
                () => selectOption(option),
                option.sku ? t`Add to Your Suite` : t`Continue`,
            );
    };

    return (
        <section className={styles.screen}>
            <div className={styles.body}>
                {screen.subcopy && (
                    <p className={styles.eyebrow}>{screen.subcopy}</p>
                )}
                <h2 className={styles.prompt}>{screen.prompt}</h2>

                <div
                    className={styles.optionsGrid}
                    style={{
                        "--columns": Math.min(screen.options.length, 3),
                    }}
                >
                    {screen.options.map((opt) => (
                        <OptionTile
                            key={opt.id}
                            option={opt}
                            onClick={() => selectOption(opt)}
                            onShowDetails={detailsHandlerFor(opt)}
                        />
                    ))}
                </div>
            </div>

            <ModalFooter
                state={state}
                onShowOfferDetails={onShowOfferDetails}
                left={
                    isBackVisible(walk) ? (
                        <ButtonPrimary
                            onClick={() => dispatch({ type: "BACK" })}
                        >
                            {t`Back`}
                        </ButtonPrimary>
                    ) : undefined
                }
                right={
                    isAny ? (
                        <ButtonPrimary
                            onClick={() => dispatch({ type: "SUBMIT_ANY" })}
                        >
                            <ResponsiveLabel
                                full={
                                    anySelectedCount === 0
                                        ? t`Skip Accessories`
                                        : t`Continue`
                                }
                                short={
                                    anySelectedCount === 0 ? t`Skip` : t`Next`
                                }
                            />
                        </ButtonPrimary>
                    ) : walk.entry === "initial" ? (
                        // Initial walks: surface the canonical-walk Skip
                        // ("Skip to [Next Spoke / Sub-Flow]" or "Skip and
                        // Complete"). Provisional picks are abandonable.
                        <ButtonPrimary
                            onClick={() => dispatch({ type: "SKIP_SPOKE" })}
                        >
                            <ResponsiveLabel
                                full={skipLabelFor(getSkipTarget(walk))}
                                short={t`Skip`}
                            />
                        </ButtonPrimary>
                    ) : (
                        // Populated walks: no canonical sequence to advance
                        // through, so the CTA abandons and returns to
                        // Populated Landing. Equivalent to the chrome close (×).
                        <ButtonPrimary
                            onClick={() => dispatch({ type: "CLOSE_BUILDER" })}
                        >
                            <ResponsiveLabel
                                full={t`Back to My Suite`}
                                short={t`Back`}
                            />
                        </ButtonPrimary>
                    )
                }
            />
        </section>
    );
};
